/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Explore customer profiles", description = "Look up and inspect individual members of a parent model — attributes, events, traits, and audience memberships — without building an audience first.", lastUpdatedDate = new Date(1787702400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Explore customer profiles",
        description: "Look up and inspect individual members of a parent model — attributes, events, traits, and audience memberships — without building an audience first.",
        lastUpdatedDate: "Wed Aug 26 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Find a member",
          "depth": 0,
          "id": "find-a-member"
        }, {
          "value": "Inspect a member",
          "depth": 0,
          "id": "inspect-a-member"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      img: "img",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components), {FeatureFlag} = _components;
    if (!FeatureFlag) _missingMdxReference("FeatureFlag", true);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers and analytics/data engineers"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsxs(_components.td, {
              children: ["A configured ", _jsx(_components.a, {
                href: "/customer-studio/schema",
                children: "schema"
              }), " with at least one parent model."]
            })]
          })]
        })]
      }), "\n", _jsx(FeatureFlag, {
        featureName: "Profiles"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Look up and inspect individual members of a parent model — their attributes, recent events, traits, and audience memberships — without building an audience first."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.strong, {
          children: "Profiles"
        }), " (titled ", _jsx(_components.strong, {
          children: "Profile Explorer"
        }), " in the app) to explore members of your parent model directly: to understand which attributes are available, or to look up a specific customer and troubleshoot their behavior."]
      }), "\n", _jsxs(_components.p, {
        children: ["Unlike ", _jsx(_components.a, {
          href: "/customer-studio/insights",
          children: "Audience insights"
        }), ", which analyze a specific audience while you build it, Profiles doesn't require an audience in progress."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "find-a-member",
        children: "Find a member"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to ", _jsx(_components.strong, {
          children: "Customer Studio > Profiles"
        }), " and select a parent model. Search members by primary key, or select ", _jsx(_components.strong, {
          children: "Filter"
        }), " to locate members using any condition available in the audience builder."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/profiles/profile-explorer-list.png",
          alt: "The Profile Explorer member list for a parent model"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "inspect-a-member",
        children: "Inspect a member"
      }), "\n", _jsx(_components.p, {
        children: "Select a member to open their profile, which gathers that member's attributes, activity, and memberships in one place."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/profiles/member-profile-overview.png",
          alt: "A member's profile with attributes and key metrics"
        })
      }), "\n", _jsx(_components.p, {
        children: "From a profile you can:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Read their attributes and metrics"
          }), " — The fields on the parent model, alongside calculated metrics such as total order count."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "See recent activity"
          }), " — Events from the last 30 days by default, such as purchases, email opens, and page views."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Check audience and journey membership"
          }), " — The audiences and journeys the member belongs to and when they entered each, along with when their audiences last synced."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Trace related records"
          }), " — How many rows the member has in related models, such as orders or products."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/profiles/member-profile-activity.png",
          alt: "Recent events for a member over the last 30 days"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/profiles/member-profile-memberships.png",
          alt: "The audiences and journeys a member belongs to"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/profiles/member-profile-relations.png",
          alt: "Related models and how many rows the member has in each"
        })
      }), "\n", _jsx(_components.p, {
        children: "If your workspace sends messages through Hightouch, a profile also shows message delivery history and per-channel subscription status."
      }), "\n", _jsx(_components.p, {
        children: "Use a profile to see why a customer is in — or missing from — an audience: check their attributes, recent activity, and current memberships in one place."
      }), "\n", _jsxs(_components.p, {
        children: ["Columns you've chosen to ", _jsx(_components.a, {
          href: "/customer-studio/schema#set-privacy-levels-for-columns",
          children: "redact"
        }), " stay redacted here, the same as in audience previews."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/usage",
            children: "Build an audience"
          }), " once you know which members to target."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/audience-filters",
            children: "Audience filters"
          }), " for the conditions available in the ", _jsx(_components.strong, {
            children: "Filter"
          }), " option."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/insights",
            children: "Audience insights"
          }), " to analyze a specific audience's composition."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
